import {useAuthStore} from "@/stores/auth";

const baseUrl = import.meta.env.VITE_API_URL

// Helper function to decode JWT and check expiration
function isTokenExpired(token: string): boolean {
    if (!token) return true;

    try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        const currentTime = Date.now() / 1000;
        return payload.exp < currentTime;
    } catch (error) {
        return true;
    }
}

// Helper function to refresh token
async function refreshAccessToken(): Promise<boolean> {
    const authStore = useAuthStore();
    const refreshToken =
        localStorage.getItem("refreshToken") || authStore.refreshToken;

    if (!refreshToken || isTokenExpired(refreshToken)) {
        authStore.logout();
        return false;
    }

    try {
        const response = await fetch(
            `${import.meta.env.VITE_API_URL}/auth/refresh-token`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({refreshToken}),
            }
        );

        if (!response.ok) {
            throw new Error("Refresh token failed");
        }

        const data = await response.json();

        // Update tokens using store method
        authStore.updateTokens(data.accessToken, data.refreshToken);

        return true;
    } catch (error) {
        authStore.logout();
        return false;
    }
}

export const fetchWrapper = {
    get: request("GET"),
    post: request("POST"),
    put: request("PUT"),
    patch: request("PATCH"),
    delete: request("DELETE"),
    getText: textRequest("GET"),
    getBlob: blobRequest("GET"),
    filePost: fileRequest("POST"),
    filePut: fileRequest("PUT"),
    filePatch: fileRequest("PATCH"),
};

function request(method: string) {
    return async (url: any, body?: any) => {
        const authStore = useAuthStore();
        const headers = await authHeader(url);
        const isApiUrl = url.startsWith(import.meta.env.VITE_API_URL);

        // Si hay usuario logueado pero no se pudo obtener token, la sesión es inválida
        if (isApiUrl && authStore.user && !headers.Authorization) {
            authStore.logout();
            return Promise.reject("Sesión expirada");
        }

        const requestOptions: any = {
            method,
            headers,
        };
        if (body) {
            requestOptions.headers["Content-Type"] = "application/json";
            requestOptions.body = JSON.stringify(body);
        }
        return fetch(url, requestOptions).then(handleResponse);
    };
}

function fileRequest(method: string) {
    return async (url: any, body?: any) => {
        const authStore = useAuthStore();
        const headers = await authHeader(url);
        const isApiUrl = url.startsWith(import.meta.env.VITE_API_URL);

        // Si hay usuario logueado pero no se pudo obtener token, la sesión es inválida
        if (isApiUrl && authStore.user && !headers.Authorization) {
            authStore.logout();
            return Promise.reject("Sesión expirada");
        }

        const requestOptions: any = {
            method,
            headers,
        };
        if (body) {
            requestOptions.body = body;
        }
        return fetch(url, requestOptions).then(handleResponse);
    };
}

export interface blobRequestResponse {
    blob: Blob;
    filename: string;
}

function blobRequest(method: string) {
    return async (url: any): Promise<blobRequestResponse> => {
        const authStore = useAuthStore();
        const headers = await authHeader(url);
        const isApiUrl = url.startsWith(import.meta.env.VITE_API_URL);

        if (isApiUrl && authStore.user && !headers.Authorization) {
            authStore.logout();
            return Promise.reject("Sesión expirada");
        }

        const requestOptions: any = {method, headers};
        const response = await fetch(url, requestOptions);

        if (!response.ok) {
            if (response.status === 401) authStore.logout();
            const text = await response.text();
            let message = response.statusText;
            try {
                const data = JSON.parse(text);
                message = data?.message || message;
            } catch { /* no-op */
            }
            return Promise.reject(message);
        }
        const filename = response.headers.get("Content-Disposition")?.split("filename=")[1]?.replace(/"/g, "") || "file";

        const blob = await response.blob();
        return {blob, filename};
    };
}

function textRequest(method: string) {
    return async (url: any) => {
        const authStore = useAuthStore();
        const headers = await authHeader(url);
        const isApiUrl = url.startsWith(import.meta.env.VITE_API_URL);

        if (isApiUrl && authStore.user && !headers.Authorization) {
            authStore.logout();
            return Promise.reject("Sesión expirada");
        }

        const requestOptions: any = {
            method,
            headers,
        };

        const response = await fetch(url, requestOptions);

        if (!response.ok) {
            if (response.status === 401) {
                authStore.logout();
            }
            // if (response.status === 403 && authStore.user) {
            //   authStore.logout();
            // }
            const text = await response.text();
            let message = response.statusText;
            try {
                const data = JSON.parse(text);
                message = data?.message || message;
            } catch { /* no-op */
            }
            return Promise.reject(message);
        }

        return response.text();
    };
}


// helper functions

async function authHeader(url: any) {
    // return auth header with jwt if user is logged in and request is to the api url
    const authStore = useAuthStore();
    const isApiUrl = url.startsWith(import.meta.env.VITE_API_URL);

    if (!isApiUrl) {
        return {};
    }

    // Si no hay usuario logueado, no enviar token (permite peticiones públicas como login)
    if (!authStore.user) {
        return {};
    }

    let accessToken =
        localStorage.getItem("accessToken") || authStore.accessToken;

    // Si no hay token disponible, no intentar nada
    if (!accessToken) {
        return {};
    }

    // Check if access token is expired
    if (isTokenExpired(accessToken)) {
        // Try to refresh the token
        const refreshSuccess = await refreshAccessToken();
        if (refreshSuccess) {
            accessToken =
                localStorage.getItem("accessToken") || authStore.accessToken;
        } else {
            return {};
        }
    }

    if (accessToken) {
        return {Authorization: `Bearer ${accessToken}`};
    }

    return {};
}

function handleResponse(response: any) {
    return response.text().then((text: any) => {
        const data = text && JSON.parse(text);

        if (!response.ok) {
            const authStore = useAuthStore();

            if (response.status === 401) {
                // Token might be expired, the refresh should have been handled in authHeader
                // If we still get 401, it means refresh failed or refresh token is also expired
                authStore.logout();
            }

            // if (response.status === 403 && authStore.user) {
            //   // Forbidden - user doesn't have permission
            //   authStore.logout();
            // }

            const error = (data && data.message) || response.statusText;
            return Promise.reject(error);
        }

        return data;
    });
}
